// Keep the screen on during the call (design 7.8: a locked phone drops the call).
// Supported in Chrome and in Safari 16.4 and later; where it is missing the
// voice-only screen still says "Keep this screen open during the call". The
// browser releases the lock whenever the page is hidden, so it is taken again
// each time the page comes back.

type Sentinel = { release(): Promise<void>; released?: boolean };

export function createWakeLock(nav: Navigator | undefined = typeof navigator !== "undefined" ? navigator : undefined) {
  let sentinel: Sentinel | null = null;
  let wanted = false;

  const request = async (): Promise<boolean> => {
    const api = (nav as (Navigator & { wakeLock?: { request(type: "screen"): Promise<Sentinel> } }) | undefined)
      ?.wakeLock;
    if (!api || !wanted) return false;
    if (sentinel && !sentinel.released) return true;
    try {
      sentinel = await api.request("screen");
      return true;
    } catch {
      // Refused (battery saver, not visible yet). The page says to keep the screen open.
      sentinel = null;
      return false;
    }
  };

  const onVisible = () => {
    if (typeof document !== "undefined" && document.visibilityState === "visible") void request();
  };

  return {
    get supported() {
      return Boolean((nav as (Navigator & { wakeLock?: unknown }) | undefined)?.wakeLock);
    },
    async acquire(): Promise<boolean> {
      wanted = true;
      if (typeof document !== "undefined") document.addEventListener("visibilitychange", onVisible);
      return request();
    },
    release() {
      wanted = false;
      if (typeof document !== "undefined") document.removeEventListener("visibilitychange", onVisible);
      const current = sentinel;
      sentinel = null;
      void current?.release().catch(() => undefined);
    },
  };
}
